import { ImageMissingPlaceholder } from '@vs-shop/ui/imageMissingPlaceholder';
import { PriceTagCurrent, PriceTagOriginal } from '@vs-shop/ui/priceTag';
import { Skeleton } from '@vs-shop/ui/skeleton';
import { clsxm } from '@vs-shop/utils';

import {
  ProductCard,
  ProductCardActionIcons,
  ProductCardActions,
  ProductCardDescription,
  ProductCardFooter,
  ProductCardHeader,
  ProductCardImagePlaceholder,
  ProductCardMedia,
  ProductCardPrice,
  ProductCardPriceRow,
  ProductCardPromo,
  ProductCardStock,
  ProductCardStockNote,
  ProductCardStockStatus,
  type ProductCardVariant,
} from '@vs-shop/commerce-ui/productCard';

import type { ClassValue } from 'clsx';

type Props = {
  variant: ProductCardVariant;
  className?: ClassValue;
};

/**
 * The card's loading state.
 *
 * Composed from the same `@vs-shop/commerce-ui` parts as
 * {@link ProductCard} and in the same order, so every height, padding and
 * appearance step comes from the card's own recipes rather than a copy of them.
 * A skeleton that restates those numbers drifts the moment the card changes,
 * and the drift is invisible: both render, they just no longer agree, and the
 * grid jumps when the products arrive.
 *
 * What this file decides is only where the grey blocks sit inside those parts
 * and how wide they are.
 */
export default function ProductCardSkeleton({ variant, className }: Props) {
  return (
    <ProductCard variant={variant} className={clsxm(className)}>
      <div>
        {/* NAME — two lines, the taller case the header reserves */}
        <ProductCardHeader>
          <Skeleton className="mb-1.5 h-5 w-full rounded" />
          <Skeleton className="h-5 w-3/4 rounded" />
        </ProductCardHeader>

        {/* COVER IMAGE */}
        <ProductCardMedia variant={variant}>
          <ProductCardImagePlaceholder>
            <ImageMissingPlaceholder />
          </ProductCardImagePlaceholder>
        </ProductCardMedia>
      </div>

      {/* DISCOUNT CODE — the strip keeps its place, empty */}
      <ProductCardPromo />

      {/* DESCRIPTION */}
      <ProductCardDescription variant={variant}>
        <Skeleton className="mb-1.5 h-3.5 w-full rounded" />
        <Skeleton className="mb-1.5 h-3.5 w-full rounded" />
        <Skeleton className="h-3.5 w-4/5 rounded" />
      </ProductCardDescription>

      {/* FOOTER */}
      <ProductCardFooter variant={variant}>
        <ProductCardPriceRow variant={variant}>
          <ProductCardPrice variant={variant}>
            <PriceTagOriginal />
            <PriceTagCurrent>
              <Skeleton className="h-[22px] w-28 rounded" />
            </PriceTagCurrent>
          </ProductCardPrice>

          <ProductCardStock variant={variant}>
            <ProductCardStockNote variant={variant}>
              <Skeleton className="h-3.5 w-28 rounded" />
            </ProductCardStockNote>
            <ProductCardStockStatus>
              <Skeleton className="h-3.5 w-20 rounded" />
            </ProductCardStockStatus>
          </ProductCardStock>
        </ProductCardPriceRow>

        {/* The detail control and the two icon controls beside it, at the sizes
            `@vs-shop/ui`'s Button paints them: `size="base"` and `size="icon"`. */}
        <ProductCardActions variant={variant}>
          <Skeleton className="h-12 flex-1 rounded-lg" />
          <ProductCardActionIcons>
            <Skeleton className="size-12 rounded-lg" />
            <Skeleton className="size-12 rounded-lg" />
          </ProductCardActionIcons>
        </ProductCardActions>
      </ProductCardFooter>
    </ProductCard>
  );
}
